import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import type { Story, StoryInsert, StoryStep } from '@/types/database'

// Random emoji covers for new stories
const STORY_EMOJIS = ['📖', '✨', '🌟', '🚀', '🦋', '🌈', '🏰', '🐉', '🦄', '🌸', '🎭', '🎪']

function getRandomEmoji(): string {
  return STORY_EMOJIS[Math.floor(Math.random() * STORY_EMOJIS.length)]
}

export async function getStoriesForChild(childId: string): Promise<Story[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('stories')
    .select('*')
    .eq('child_id', childId)
    .is('deleted_at', null)
    .order('updated_at', { ascending: false })

  if (error) {
    console.error('Error fetching stories:', error)
    throw error
  }

  return (data as Story[]) || []
}

export async function getStoryWithSteps(storyId: string): Promise<{ story: Story; steps: StoryStep[] } | null> {
  if (!isSupabaseConfigured()) return null

  const [storyResult, stepsResult] = await Promise.all([
    supabase
      .from('stories')
      .select('*')
      .eq('id', storyId)
      .is('deleted_at', null)
      .single(),
    supabase
      .from('story_steps')
      .select('*')
      .eq('story_id', storyId)
      .is('deleted_at', null)
      .order('step_order', { ascending: true }),
  ])

  if (storyResult.error) {
    console.error('Error fetching story:', storyResult.error)
    throw storyResult.error
  }

  if (stepsResult.error) {
    console.error('Error fetching steps:', stepsResult.error)
    throw stepsResult.error
  }

  return {
    story: storyResult.data as Story,
    steps: (stepsResult.data as StoryStep[]) || [],
  }
}

export async function createStory(
  childId: string,
  title: string = 'My New Story'
): Promise<Story | null> {
  if (!isSupabaseConfigured()) return null

  const { data, error } = await supabase
    .from('stories')
    .insert({
      child_id: childId,
      title,
      cover_emoji: getRandomEmoji(),
    } as Record<string, unknown>)
    .select()
    .single()

  if (error) {
    console.error('Error creating story:', error)
    throw error
  }

  return data as Story
}

export async function updateStory(
  storyId: string,
  updates: Partial<StoryInsert>
): Promise<Story | null> {
  if (!isSupabaseConfigured()) return null

  const { data, error } = await supabase
    .from('stories')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', storyId)
    .select()
    .single()

  if (error) {
    console.error('Error updating story:', error)
    throw error
  }

  return data as Story
}

export async function updateStoryStep(
  stepId: string,
  content: string
): Promise<StoryStep | null> {
  if (!isSupabaseConfigured()) return null

  const wordCount = content.trim().split(/\s+/).filter(Boolean).length

  const { data, error } = await supabase
    .from('story_steps')
    .update({
      content,
      word_count: wordCount,
      updated_at: new Date().toISOString(),
      client_rev: Date.now(),
    } as Record<string, unknown>)
    .eq('id', stepId)
    .select()
    .single()

  if (error) {
    console.error('Error updating step:', error)
    throw error
  }

  const step = data as StoryStep

  // Update parent story's total word count
  await updateStoryWordCount(step.story_id)

  return step
}

/**
 * Recalculates and updates the total word count for a story
 */
async function updateStoryWordCount(storyId: string): Promise<void> {
  if (!isSupabaseConfigured()) return

  // Sum all step word counts
  const { data: steps } = await supabase
    .from('story_steps')
    .select('word_count')
    .eq('story_id', storyId)
    .is('deleted_at', null)

  const totalWords = (steps || []).reduce((sum, step) => sum + (step.word_count || 0), 0)

  await supabase
    .from('stories')
    .update({
      word_count: totalWords,
      updated_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', storyId)
}

export async function deleteStory(storyId: string): Promise<void> {
  if (!isSupabaseConfigured()) return

  // Soft delete by setting deleted_at
  const { error } = await supabase
    .from('stories')
    .update({ deleted_at: new Date().toISOString() } as Record<string, unknown>)
    .eq('id', storyId)

  if (error) {
    console.error('Error deleting story:', error)
    throw error
  }
}

/**
 * Recalculates word counts for all stories belonging to a child.
 * Also updates the child's total_words_written field.
 * Useful for fixing stories created before word count propagation was added.
 */
export async function recalculateStoryWordCounts(childId: string): Promise<void> {
  if (!isSupabaseConfigured()) return

  // Get all stories for this child
  const { data: stories, error: storiesError } = await supabase
    .from('stories')
    .select('id')
    .eq('child_id', childId)
    .is('deleted_at', null)

  if (storiesError) {
    console.error('Error fetching stories for recalculation:', storiesError)
    return
  }

  if (!stories || stories.length === 0) return

  let grandTotal = 0

  // Recalculate word count for each story
  for (const story of stories) {
    const { data: steps } = await supabase
      .from('story_steps')
      .select('word_count')
      .eq('story_id', story.id)
      .is('deleted_at', null)

    const totalWords = (steps || []).reduce((sum, step) => sum + (step.word_count || 0), 0)
    grandTotal += totalWords

    await supabase
      .from('stories')
      .update({
        word_count: totalWords,
        updated_at: new Date().toISOString(),
      } as Record<string, unknown>)
      .eq('id', story.id)
  }

  // Update the child's total_words_written
  await supabase
    .from('children')
    .update({
      total_words_written: grandTotal,
    } as Record<string, unknown>)
    .eq('id', childId)
}
